import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { parseSearchQuery } from '../hooks/useAdvancedSearch';
import { ulid } from 'ulid';

export interface WorkerTag {
  key: string;
  value: string;
}

export interface LanguageLevel {
  language: string;
  level: 'A' | 'B' | 'C';
}

export interface Worker {
  id: string;
  name: string;
  email: string;
  phone?: string;
  skills: string[];
  hourlyRate: number;
  currency: string;
  status: 'active' | 'inactive';
  avatar?: string;
  joinedAt: string;
  lastActiveAt?: string;
  isOnline: boolean;
  totalTasks: number;
  memo?: string;
  
  // 단가 정보
  finalRate: number; // 최종 단가
  averageRate: number; // 평균 단가
  completedTasksCount: number; // 작업 수
  
  // 언어 등급
  languageLevels: LanguageLevel[];
  
  // 태그
  tags: WorkerTag[];
}

interface WorkerStore {
  workers: Worker[];
  isLoading: boolean;
  searchQuery: string;
  statusFilter: 'all' | 'active' | 'inactive';
  
  // Actions
  loadWorkers: () => Promise<void>;
  createWorker: (worker: Omit<Worker, 'id' | 'joinedAt' | 'totalTasks' | 'finalRate' | 'averageRate' | 'completedTasksCount' | 'isOnline'>) => Promise<void>;
  updateWorker: (id: string, updates: Partial<Worker>) => Promise<void>;
  deleteWorker: (id: string) => Promise<void>;
  setSearchQuery: (query: string) => void;
  setStatusFilter: (status: 'all' | 'active' | 'inactive') => void;
  getFilteredWorkers: () => Worker[];
  updateOnlineStatus: (id: string, isOnline: boolean) => void;
}

// 샘플 데이터
const sampleWorkers: Worker[] = [
  {
    id: '01JGHPRFRX3NKFRQ8ZVTXSYQ7X',
    name: '김민수',
    email: 'minsu.kim@example.com',
    phone: '+82-10-1234-5678',
    skills: ['한국어', '영어', '번역', '검수'],
    hourlyRate: 25000,
    currency: 'KRW',
    status: 'active',
    avatar: '👨‍💻',
    joinedAt: '2023-01-15',
    lastActiveAt: '2024-01-22T10:30:00Z',
    isOnline: true,
    totalTasks: 156,
    memo: '한영 번역 전문가. 기술 문서 번역에 특화되어 있음.',
    finalRate: 27000,
    averageRate: 25500,
    completedTasksCount: 156,
    languageLevels: [
      { language: 'KO', level: 'A' },
      { language: 'EN', level: 'A' }
    ],
    tags: [
      { key: '전문분야', value: '기술번역' },
      { key: '경력', value: '5년' },
      { key: '자격증', value: 'TOEIC 950' }
    ]
  },
  {
    id: '01JGHPRGTW6PKFRN4BVTXSYQ8Y',
    name: '이영희',
    email: 'younghee.lee@example.com',
    phone: '+82-10-2345-6789',
    skills: ['한국어', '일본어', '번역', '통역'],
    hourlyRate: 30000,
    currency: 'KRW',
    status: 'active',
    avatar: '👩‍💼',
    joinedAt: '2023-03-20',
    lastActiveAt: '2024-01-22T15:45:00Z',
    isOnline: true,
    totalTasks: 89,
    memo: '한일 번역 및 통역 전문가. 비즈니스 미팅 통역 경험 다수.',
    finalRate: 32000,
    averageRate: 30800,
    completedTasksCount: 89,
    languageLevels: [
      { language: 'KO', level: 'A' },
      { language: 'JA', level: 'A' }
    ],
    tags: [
      { key: '전문분야', value: '비즈니스통역' },
      { key: '경력', value: '7년' },
      { key: '자격증', value: 'JLPT N1' }
    ]
  },
  {
    id: '01JGHPRHVZ9QKFRP6CVTXSYQ9Z',
    name: 'John Smith',
    email: 'john.smith@example.com',
    phone: '+1-555-123-4567',
    skills: ['English', 'Korean', 'Translation', 'Proofreading'],
    hourlyRate: 35,
    currency: 'USD',
    status: 'active',
    avatar: '👨‍🎓',
    joinedAt: '2023-02-10',
    lastActiveAt: '2024-01-21T09:15:00Z',
    isOnline: false,
    totalTasks: 203,
    memo: '영어 원어민. 한국어 능통. 학술 논문 번역 전문.',
    finalRate: 38,
    averageRate: 36,
    completedTasksCount: 203,
    languageLevels: [
      { language: 'EN', level: 'A' },
      { language: 'KO', level: 'B' }
    ],
    tags: [
      { key: '전문분야', value: '학술번역' },
      { key: '경력', value: '10년' },
      { key: '학위', value: 'Ph.D' }
    ]
  },
  {
    id: '01JGHPRJX2CRJKFR8DVTXSYQ1A',
    name: '王小明',
    email: 'xiaoming.wang@example.com',
    phone: '+86-138-0013-8000',
    skills: ['中文', '한국어', '번역', '전사'],
    hourlyRate: 28000,
    currency: 'KRW',
    status: 'active',
    avatar: '👨‍🔬',
    joinedAt: '2023-04-05',
    lastActiveAt: '2024-01-20T14:20:00Z',
    isOnline: false,
    totalTasks: 124,
    memo: '한중 번역 및 전사 전문가. 무역 문서 번역 경험 풍부.',
    finalRate: 29000,
    averageRate: 28200,
    completedTasksCount: 124,
    languageLevels: [
      { language: 'ZH', level: 'A' },
      { language: 'KO', level: 'A' }
    ],
    tags: [
      { key: '전문분야', value: '무역번역' },
      { key: '경력', value: '6년' },
      { key: '자격증', value: 'HSK 6급' }
    ]
  },
  {
    id: '01JGHPRKZ5FSKFRQ2EVTXSYQ2B',
    name: '박지은',
    email: 'jieun.park@example.com',
    phone: '+82-10-3456-7890',
    skills: ['한국어', '영어', '전사', '검수'],
    hourlyRate: 22000,
    currency: 'KRW',
    status: 'inactive',
    avatar: '👩‍🎨',
    joinedAt: '2023-05-15',
    lastActiveAt: '2023-12-10T16:30:00Z',
    isOnline: false,
    totalTasks: 67,
    memo: '전사 및 검수 전문가. 현재 휴직 중.',
    finalRate: 23000,
    averageRate: 22300,
    completedTasksCount: 67,
    languageLevels: [
      { language: 'KO', level: 'A' },
      { language: 'EN', level: 'B' }
    ],
    tags: [
      { key: '전문분야', value: '전사검수' },
      { key: '경력', value: '3년' },
      { key: '상태', value: '휴직중' }
    ]
  },
  {
    id: '01JGHPRM18HTKFRN4FVTXSYQ3C',
    name: 'Maria Garcia',
    email: 'maria.garcia@example.com',
    phone: '+34-612-345-678',
    skills: ['Español', 'English', 'Translation', 'Localization'],
    hourlyRate: 32,
    currency: 'EUR',
    status: 'active',
    avatar: '👩‍🏫',
    joinedAt: '2023-06-01',
    lastActiveAt: '2024-01-22T11:00:00Z',
    isOnline: true,
    totalTasks: 78,
    memo: '스페인어-영어 번역 및 현지화 전문가.',
    finalRate: 35,
    averageRate: 33,
    completedTasksCount: 78,
    languageLevels: [
      { language: 'ES', level: 'A' },
      { language: 'EN', level: 'A' }
    ],
    tags: [
      { key: '전문분야', value: '현지화' },
      { key: '경력', value: '8년' },
      { key: '자격증', value: 'DELE C2' }
    ]
  }
];

export const useWorkerStore = create<WorkerStore>()(
  devtools(
    (set, get) => ({
      workers: sampleWorkers,
      isLoading: false,
      searchQuery: '',
      statusFilter: 'all',

      loadWorkers: async () => {
        set({ isLoading: true });
        try {
          // API 호출 시뮬레이션
          await new Promise(resolve => setTimeout(resolve, 500));
          // 실제로는 API에서 데이터를 가져옴
          set({ workers: sampleWorkers });
        } catch (error) {
          console.error('작업자 목록 로딩 실패:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      createWorker: async (workerData) => {
        set({ isLoading: true });
        try {
          const newWorker: Worker = {
            ...workerData,
            id: ulid(),
            joinedAt: new Date().toISOString().split('T')[0],
            totalTasks: 0,
            isOnline: false,
            finalRate: workerData.hourlyRate,
            averageRate: workerData.hourlyRate,
            completedTasksCount: 0,
            languageLevels: workerData.languageLevels || [],
            tags: workerData.tags || []
          };
          
          set(state => ({
            workers: [...state.workers, newWorker]
          }));
          
          console.log('작업자 생성 완료:', newWorker);
        } catch (error) {
          console.error('작업자 생성 실패:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateWorker: async (id, updates) => {
        set({ isLoading: true });
        try {
          set(state => ({
            workers: state.workers.map(worker =>
              worker.id === id ? { ...worker, ...updates } : worker
            )
          }));
          
          console.log('작업자 수정 완료:', id, updates);
        } catch (error) {
          console.error('작업자 수정 실패:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      deleteWorker: async (id) => {
        set({ isLoading: true });
        try {
          set(state => ({
            workers: state.workers.filter(worker => worker.id !== id)
          }));
          
          console.log('작업자 삭제 완료:', id);
        } catch (error) {
          console.error('작업자 삭제 실패:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      setSearchQuery: (query) => {
        set({ searchQuery: query });
      },

      setStatusFilter: (status) => {
        set({ statusFilter: status });
      },

      updateOnlineStatus: (id, isOnline) => {
        set(state => ({
          workers: state.workers.map(worker =>
            worker.id === id 
              ? { ...worker, isOnline, lastActiveAt: new Date().toISOString() }
              : worker
          )
        }));
      },

      getFilteredWorkers: () => {
        const { workers, searchQuery, statusFilter } = get();
        
        return workers.filter(worker => {
          // 상태 필터
          if (statusFilter !== 'all' && worker.status !== statusFilter) {
            return false;
          }
          
          // 검색 쿼리
          if (searchQuery) {
            const tokens = parseSearchQuery(searchQuery);
            
            // 모든 토큰이 AND 조건으로 만족해야 함
            return tokens.every(token => {
              const value = token.value.toLowerCase();
              
              if (token.type === 'key-value' && token.key) {
                const key = token.key.toLowerCase();
                
                // 언어등급 검색: 언어:A, EN:B 형태
                if (key.match(/^[a-z]{2,3}$/) && value.match(/^[abc]$/i)) {
                  return worker.languageLevels?.some(lang => 
                    lang.language.toLowerCase() === key && 
                    lang.level.toLowerCase() === value
                  ) || false;
                }
                
                // 특정 키 매핑
                switch (key) {
                  case '이름':
                  case 'name':
                    return worker.name.toLowerCase().includes(value);
                  
                  case '이메일':
                  case 'email':
                    return worker.email.toLowerCase().includes(value);
                  
                  case '메모':
                  case 'memo':
                    return worker.memo?.toLowerCase().includes(value) || false;
                  
                  case '스킬':
                  case 'skill':
                  case 'skills':
                    return worker.skills.some(skill => skill.toLowerCase().includes(value));
                  
                  case '상태':
                  case 'status':
                    return worker.status.toLowerCase().includes(value);
                  
                  default:
                    // 태그 검색 (부분 일치)
                    return worker.tags?.some(tag => 
                      tag.key.toLowerCase().includes(key) && 
                      tag.value.toLowerCase().includes(value)
                    ) || false;
                }
              } else {
                // 일반 검색 (모든 필드)
                return (
                  worker.name.toLowerCase().includes(value) ||
                  worker.email.toLowerCase().includes(value) ||
                  worker.skills.some(skill => skill.toLowerCase().includes(value)) ||
                  (worker.memo && worker.memo.toLowerCase().includes(value)) ||
                  worker.languageLevels?.some(lang => 
                    lang.language.toLowerCase().includes(value) ||
                    `${lang.language.toLowerCase()}:${lang.level.toLowerCase()}`.includes(value)
                  ) ||
                  worker.tags?.some(tag => 
                    tag.key.toLowerCase().includes(value) ||
                    tag.value.toLowerCase().includes(value) ||
                    `${tag.key}:${tag.value}`.toLowerCase().includes(value)
                  )
                );
              }
            });
          }
          
          return true;
        });
      }
    }),
    { name: 'worker-store' }
  )
);
